import React, { useState, useEffect, useCallback, useRef } from 'react'
import { DateRangePicker, Calendar } from 'react-date-range'
import ptLocale from 'react-date-range/dist/locale/pt'
import { Icon, Popover } from 'antd'
import moment from 'moment'
import 'moment/locale/pt-br'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import PropTypes from 'prop-types'
import { defaultStaticRanges } from 'components/DatePicker/definedDates'
import css from './index.scss'
import zIndex from '@material-ui/core/styles/zIndex'

const defaultContainerStyle = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
}
const DefaultDatePicker = ({
  value,
  onChange,
  placeholder = 'Selecione um período',
  localeFormat = 'DD/MM/YYYY',
  localeSeparator = ' - ',
  disabled = false,
  minDate,
  maxDate,
  containerStyle,
  inputStyle = {},
  showCalendarIcon = true,
  mode = 'range',
  allowClear = true,
}) => {
  const [selection, setSelection] = useState(
    mode === 'range'
      ? {
          startDate: moment().toDate(),
          endDate: moment().toDate(),
          key: 'selection',
        }
      : {
          date: moment().toDate(),
        }
  )
  const [isHoveredInput, setIsHoveredInput] = useState(false)

  const parseDate = useCallback(
    (dateStr) => {
      const m = moment(dateStr, localeFormat)
      return m.isValid() ? m.toDate() : moment().toDate()
    },
    [localeFormat]
  )

  useEffect(() => {
    if (mode === 'range') {
      setSelection({
        startDate: parseDate(value?.startDate),
        endDate: parseDate(value?.endDate),
        key: 'selection',
      })
    } else {
      setSelection({
        date: parseDate(value),
      })
    }
  }, [value, parseDate, mode])

  const formatDate = (date) => {
    return moment(date).locale('pt-br').format('D [de] MMMM [de] YYYY')
  }

  const formatDateOutput = (date) => {
    return moment(date).format(localeFormat)
  }

  const handleRangeChange = (ranges) => {
    if (mode === 'range') {
      const { startDate, endDate } = ranges.selection
      setSelection({ startDate, endDate, key: 'selection' })

      if (startDate && endDate && startDate.getTime() !== endDate.getTime()) {
        onChange?.({
          startDate: formatDateOutput(startDate),
          endDate: formatDateOutput(endDate),
        })
      }
    }
  }

  const handleSingleDateChange = (date) => {
    const now = new Date()
    const isToday =
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate()

    if (isToday) {
      date.setHours(now.getHours(), now.getMinutes(), now.getSeconds())
    } 
    date.setSeconds(0, 0)

    setSelection({ date })
    onChange?.(
     formatDateOutput(date),
    )
  }

  const getDisplayValue = () => {
    if (!value) {
      return placeholder
    }
    if (mode === 'range') {
      if (!value.startDate || !value.endDate) return placeholder
      return (
        <>
          <span style={{ color: '#4f4f4f' }}>{formatDate(parseDate(value.startDate))}</span>
          <span style={{ color: '#7906a9', margin: '0 4px', fontWeight: 500 }}>
            {localeSeparator}
          </span>
          <span style={{ color: '#4f4f4f' }}>{formatDate(parseDate(value.endDate))}</span>
        </>
      )
    } else {
      if (!value) return placeholder
      return <span style={{ color: '#4f4f4f' }}>{formatDate(parseDate(value))}</span>
    }
  }

  const hasValue = () => {
    if (!value) return false
    if (mode === 'range') {
      return value.startDate && value.endDate
    } else {
      return value
    }
  }

  const pickerContent = (
    <div style={{ padding: 0 }}>
      {mode === 'range' ? (
        <DateRangePicker
          locale={ptLocale}
          ranges={[selection]}
          onChange={handleRangeChange}
          className={[css.container, 'PreviewArea'].join(' ')}
          minDate={
            minDate ? parseDate(minDate) : moment().subtract(365, 'days').startOf('day').toDate()
          }
          maxDate={maxDate ? parseDate(maxDate) : moment().add(365, 'days').endOf('day').toDate()}
          color="#1890ff"
          rangeColors={['#7906a9', '#ec214b', '#3e057b']}
          showSelectionPreview={false}
          moveRangeOnFirstSelection={false}
          months={2}
          direction="horizontal"
          showMonthAndYearPickers={false}
          showDateDisplay={false}
          staticRanges={defaultStaticRanges}
          inputRanges={[]}
        />
      ) : (
        <Calendar
          locale={ptLocale}
          date={selection.date}
          onChange={handleSingleDateChange}
          months={2}
          direction="horizontal"
          color="#72069a"
          showMonthAndYearPickers={false}
          classNames={{
            monthAndYearWrapper: css.customMonthAndYearWrapper,
          }}
          minDate={
            minDate ? parseDate(minDate) : moment().subtract(365, 'days').startOf('day').toDate()
          }
          maxDate={maxDate ? parseDate(maxDate) : moment().add(365, 'days').endOf('day').toDate()}
        />
      )}
    </div>
  )

  const handleClear = (e) => {
    e.stopPropagation()
    if (mode === 'range') {
      setSelection({
        startDate: null,
        endDate: null,
        key: 'selection',
      })
      onChange?.({ startDate: null, endDate: null })
    } else {
      setSelection({ date: null })
      onChange?.(undefined)
    }
  }

  return (
    <div style={{ ...defaultContainerStyle, ...containerStyle }}>
      <Popover
        content={pickerContent}
        trigger="click"
        placement="bottomLeft"
        autoAdjustOverflow
        overlayStyle={{ padding: 0 }}
        overlayClassName={css.container}
      >
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: '6px 8px',
            borderRadius: '4px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: disabled ? '#f5f5f5' : '#fff',
            color: hasValue() ? '#000' : '#bfbfbf',
            ...inputStyle,
            position: 'relative',
          }}
          onMouseEnter={() => setIsHoveredInput(true)}
          onMouseLeave={() => setIsHoveredInput(false)}
          className={css.trigger}
        >
          {showCalendarIcon && (
            <Icon
              type="calendar"
              style={{
                marginRight: '6px',
                color: hasValue() ? '#7906a9' : '#bfbfbf',
              }}
            />
          )}
          <span className={css.label} style={{ color: hasValue() ? '#7906a9' : '#bfbfbf' }}>
            {getDisplayValue()}
          </span>
          {hasValue() && allowClear && isHoveredInput && (
            <Icon
              type="close-circle"
              theme="filled"
              className={css.closeIcon}
              onClick={handleClear}
            />
          )}
        </div>
      </Popover>
    </div>
  )
}

DefaultDatePicker.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string,
    }),
    PropTypes.shape({
      date: PropTypes.string,
    }),
  ]),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  localeFormat: PropTypes.string,
  localeSeparator: PropTypes.string,
  disabled: PropTypes.bool,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  containerStyle: PropTypes.object,
  inputStyle: PropTypes.object,
  showCalendarIcon: PropTypes.bool,
  mode: PropTypes.oneOf(['range', 'single']),
}

export default DefaultDatePicker
